import React from "react";
import { Calendar, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

const ASSET_LABELS = {
  fire_protection:   "Protecció incendis",
  gas_installation:  "Gas",
  kitchen_equipment: "Equip cuina",
  electrical:        "Elèctrica",
};

const ASSET_COLORS = {
  fire_protection:   { dot: "bg-red-400",    bg: "bg-red-50",    text: "text-red-700",    border: "border-red-200" },
  gas_installation:  { dot: "bg-orange-400", bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" },
  kitchen_equipment: { dot: "bg-blue-400",   bg: "bg-blue-50",   text: "text-blue-700",   border: "border-blue-200" },
  electrical:        { dot: "bg-yellow-400", bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200" },
};

export default function TechnicalCalendar({ fire, gas, equipment, electrical }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const events = [];

  fire.forEach(el => {
    if (el.next_revision_date) events.push({
      name: el.name, date: el.next_revision_date, type: "fire_protection",
      subtype: el.element_type?.replace(/_/g, " "), id: el.id,
    });
  });

  gas.forEach(el => {
    if (el.next_inspection_date) events.push({
      name: el.name, date: el.next_inspection_date, type: "gas_installation",
      subtype: el.installation_type?.replace(/_/g, " "), id: el.id,
    });
  });

  equipment.forEach(eq => {
    if (eq.next_maintenance_date) events.push({
      name: eq.name, date: eq.next_maintenance_date, type: "kitchen_equipment",
      subtype: eq.category, id: eq.id,
    });
  });

  (electrical || []).forEach(el => {
    if (el.next_inspection_date) events.push({
      name: el.name, date: el.next_inspection_date, type: "electrical",
      subtype: el.installation_type?.replace(/_/g, " "), id: el.id,
    });
  });

  events.sort((a, b) => new Date(a.date) - new Date(b.date));

  const overdue = events.filter(e => new Date(e.date) < today);
  const next30 = events.filter(e => {
    const d = new Date(e.date);
    return d >= today && Math.ceil((d - today) / 86400000) <= 30;
  });
  const next90 = events.filter(e => {
    const d = new Date(e.date);
    const diff = Math.ceil((d - today) / 86400000);
    return diff > 30 && diff <= 90;
  });
  const future = events.filter(e => Math.ceil((new Date(e.date) - today) / 86400000) > 90);

  function EventRow({ ev }) {
    const diff = Math.ceil((new Date(ev.date) - today) / 86400000);
    const colors = ASSET_COLORS[ev.type] || ASSET_COLORS.electrical;
    const isOverdue = diff < 0;

    return (
      <div className={`flex items-center gap-3 p-3 rounded-xl border ${isOverdue ? "bg-red-50 border-red-200" : `${colors.bg} ${colors.border}`}`}>
        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isOverdue ? "bg-red-500" : colors.dot}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{ev.name}</p>
          <p className="text-xs text-gray-500">{ASSET_LABELS[ev.type]} · {ev.subtype}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs font-medium text-gray-900">{new Date(ev.date).toLocaleDateString("ca-ES")}</p>
          {isOverdue ? (
            <span className="text-xs font-bold text-red-600">CADUCADA ({Math.abs(diff)}d)</span>
          ) : diff <= 30 ? (
            <span className="text-xs font-bold text-orange-500">En {diff} dies</span>
          ) : diff <= 90 ? (
            <span className="text-xs font-semibold text-yellow-600">En {diff} dies</span>
          ) : (
            <span className="text-xs text-green-600">En {diff} dies</span>
          )}
        </div>
      </div>
    );
  }

  function Section({ title, items, icon: Icon, iconCls }) {
    if (items.length === 0) return null;
    return (
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Icon className={`w-4 h-4 ${iconCls}`} />
          <p className="text-sm font-bold text-gray-700">{title} ({items.length})</p>
        </div>
        <div className="space-y-1.5">
          {items.map((ev, i) => <EventRow key={`${ev.id}-${i}`} ev={ev} />)}
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-center py-14 text-gray-400">
        <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-200" />
        <p>Cap revisió programada</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-3 text-center">
        {[
          { label: "Caducades", count: overdue.length, cls: "text-red-600 bg-red-50" },
          { label: "Pròxims 30d", count: next30.length, cls: "text-orange-600 bg-orange-50" },
          { label: "Pròxims 90d", count: next90.length, cls: "text-yellow-600 bg-yellow-50" },
          { label: "Futures", count: future.length, cls: "text-green-600 bg-green-50" },
        ].map(s => (
          <div key={s.label} className={`rounded-xl p-3 ${s.cls}`}>
            <p className="text-xl font-bold">{s.count}</p>
            <p className="text-xs font-medium mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <Section title="Caducades" items={overdue} icon={AlertTriangle} iconCls="text-red-500" />
      <Section title="Pròxims 30 dies" items={next30} icon={Clock} iconCls="text-orange-500" />
      <Section title="Pròxims 90 dies" items={next90} icon={Clock} iconCls="text-yellow-500" />
      <Section title="Futures (> 90 dies)" items={future} icon={CheckCircle2} iconCls="text-green-500" />
    </div>
  );
}